import { isObject, isString, mapValues } from "./objectUtils.mjs";
import { tag } from "./typeUtils.mjs";
import { versionedCodec } from "./versionedCodec.mjs";

//#region src/codec.ts
var CodecImpl = class {
	constructor(name, handlers) {
		this.name = name;
		this.handlers = handlers;
	}
	encode(input) {
		try {
			return this.handlers.encode(input);
		} catch (error) {
			if (error instanceof Error) {
				error.message = error.message + "\n in " + this.name;
				throw error;
			} else throw error;
		}
	}
	decode(input) {
		try {
			return this.handlers.decode(input);
		} catch (error) {
			if (error instanceof Error) {
				error.message = error.message + "\n in " + this.name;
				throw error;
			} else throw error;
		}
	}
};
function customCodec(name, handlers) {
	return new CodecImpl(name, handlers);
}
const stringCodec = new CodecImpl("string", {
	encode: (input) => {
		if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
		return input;
	},
	decode: (input) => {
		if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
		return input;
	}
});
const booleanCodec = new CodecImpl("boolean", {
	encode: (input) => {
		if (typeof input !== "boolean") throw new Error(`Expected boolean but instead found ${typeof input}`);
		return input;
	},
	decode: (input) => {
		if (typeof input !== "boolean") throw new Error(`Expected boolean but instead found ${typeof input}`);
		return input;
	}
});
function literalCodec(...values) {
	if (values.length === 0) throw new Error("literalCodec requires at least one value");
	return new CodecImpl(values.length === 1 ? `literal("${values[0]}")` : `literal(${values.map((v) => `"${v}"`).join(", ")})`, {
		encode: (input) => {
			if (!values.includes(input)) {
				const expectedText = values.length === 1 ? `${values[0]}` : `one of ${values.join(", ")}`;
				throw new Error(`Expected ${expectedText} but instead found ${input}`);
			}
			return input;
		},
		decode: (input) => {
			if (!values.includes(input)) {
				const expectedText = values.length === 1 ? `${values[0]}` : `one of ${values.join(", ")}`;
				throw new Error(`Expected ${expectedText} but instead found ${input}`);
			}
			return input;
		}
	});
}
const dateCodec = new CodecImpl("date", {
	encode: (input) => {
		if (!(input instanceof Date)) throw new Error(`Expected date but instead found ${typeof input}`);
		return input.toISOString();
	},
	decode: (input) => {
		if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
		const dateMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (dateMatch && dateMatch[1] && dateMatch[2] && dateMatch[3]) {
			const year = parseInt(dateMatch[1]);
			const month = parseInt(dateMatch[2]);
			const day = parseInt(dateMatch[3]);
			return new Date(year, month - 1, day);
		}
		return new Date(input);
	}
});
/**
* Creates an object codec with compile-time enforcement that the shape
* exactly matches both the decoded type (I) and encoded type (O).
*
* @example
* type User = { id: UserId; name: string };
* type StorageUser = { id: string; name: string };
*
* const userCodec = codec.object<User, StorageUser>({
*   id: codec.tagged("UserId"),
*   name: codec.string,
* });
*
* // Missing field → type error
* // Extra field → type error
* // Wrong decoded type → type error
* // Wrong encoded type → type error
*/
function objectCodec(shape) {
	return new CodecImpl(`object({\n  ${Object.entries(shape).map(([key, codec]) => `${key}: ${codec.name}`).join(",\n  ")}\n})`, {
		encode: (input) => {
			if (!isObject(input)) throw new Error(`Expected object but instead found ${typeof input}`);
			return mapValues(shape, (codec, key) => codec.encode(input[key]));
		},
		decode: (input) => {
			if (!isObject(input)) throw new Error(`Expected object but instead found ${typeof input}`);
			return mapValues(shape, (codec, key) => codec.decode(input[key]));
		}
	});
}
function oneOfCodec(...codecs) {
	return new CodecImpl(`oneOf(\n  ${codecs.map((c) => c.name).join(",\n  ")}\n)`, {
		encode: (input) => {
			const errors = [];
			for (const codec of codecs) try {
				const encoded = codec.encode(input);
				if (encoded !== void 0) return encoded;
			} catch (error) {
				errors.push(`${codec.name}: ${error}`);
			}
			throw new Error(`No codec matched ${input}: \n${errors.join("\n")}`);
		},
		decode: (input) => {
			const errors = [];
			for (const codec of codecs) try {
				return codec.decode(input);
			} catch (error) {
				errors.push(`${codec.name}: ${error}`);
			}
			throw new Error(`No codec matched ${input}: \n${errors.join("\n")}`);
		}
	});
}
/**
* You should pass the enum as a value directly here. e.g.
*
* enumCodec<ActivityType>(ActivityType)
* codec.enum<ActivityType>(ActivityType)
*/
function enumCodec(enumObject) {
	return new CodecImpl(`enum(${Object.values(enumObject).map((v) => `"${v}"`).join(", ")})`, {
		encode: (input) => {
			if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
			if (!Object.values(enumObject).includes(input)) throw new Error(`Expected one of ${Object.values(enumObject).join(", ")} but instead found ${input}`);
			return input;
		},
		decode: (input) => {
			if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
			if (!Object.values(enumObject).includes(input)) throw new Error(`Expected one of ${Object.values(enumObject).join(", ")} but instead found ${input}`);
			return input;
		}
	});
}
function taggedCodec(tagName) {
	return new CodecImpl(`tagged(${tagName})`, {
		encode: (input) => {
			if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
			return input;
		},
		decode: (input) => {
			if (!isString(input)) throw new Error(`Expected string but instead found ${typeof input}`);
			return tag(input);
		}
	});
}
function nullableCodec(codec, defaultValue) {
	const hasDefault = defaultValue !== void 0;
	return new CodecImpl(`nullable(${codec.name}${hasDefault ? `, ${defaultValue}` : ""})`, {
		encode: (input) => {
			if (input === null || input === void 0) return null;
			return codec.encode(input);
		},
		decode: (input) => {
			if (input === null || input === void 0) return hasDefault ? defaultValue : void 0;
			return codec.decode(input);
		}
	});
}
function arrayCodec(itemCodec) {
	return new CodecImpl(`array(${itemCodec.name})`, {
		encode: (input) => {
			if (!Array.isArray(input)) throw new Error(`Expected array but found ${typeof input}`);
			return input.map((item) => itemCodec.encode(item));
		},
		decode: (input) => {
			if (!Array.isArray(input)) throw new Error(`Expected array but found ${typeof input}`);
			return input.map((item) => itemCodec.decode(item));
		}
	});
}
function identityCodec() {
	return new CodecImpl("identity", {
		encode: (input) => input,
		decode: (input) => input
	});
}
/**
* If decoding or encoding, just throw errors instead of using default values
*/
const codec = {
	object: objectCodec,
	oneOf: oneOfCodec,
	literal: literalCodec,
	date: dateCodec,
	nullable: nullableCodec,
	array: arrayCodec,
	string: stringCodec,
	boolean: booleanCodec,
	tagged: taggedCodec,
	enum: enumCodec,
	custom: customCodec,
	identity: identityCodec
};

//#endregion
export { codec, versionedCodec };